import { isPerformanceAdmin } from '@/shared/rbac'
import type { Tour } from '../../types'
import { target } from './target'

export const dashboardTours: Tour[] = [
  {
    id: 'page:charts-dashboard',
    version: 1,
    title: 'Charts Dashboard',
    kind: 'page',
    // Overview only: its tabs, filters, counts and export are tagged there. The
    // Pipeline tab has its own tour below (QA F2).
    match: /^\/client-admin(\/overview)?\/?$/,
    access: { gate: 'canAdminClient' }, // router: client-admin/*
    steps: [
      { id: 'title', target: target('page-header'), side: 'bottom', title: 'Charts Dashboard', body: 'How charts are moving through the pipeline for your facilities.' },
      { id: 'tabs', target: target('charts.tabs'), side: 'bottom', title: 'Overview and Pipeline', body: '"Overview" summarizes counts and processing mode; "Pipeline" shows volume by bucket and average time at each stage.' },
      { id: 'filters', target: target('charts.filters'), side: 'bottom', title: 'Filters', body: 'Pick a client, facility and time period. The cards on the page follow these filters.' },
      { id: 'counts', target: target('charts.pipeline-counts'), whenMissing: 'Chart counts for the selected period appear here once there is data for your filters.',  waitMs: 3000, side: 'top', title: 'Chart counts', body: 'How many charts were imported, are processing, and have been worked in the selected period.' },
      { id: 'export', target: target('charts.export'), waitMs: 2000, side: 'bottom', title: 'Export', body: 'Downloads this overview as a spreadsheet.' },
    ],
  },
  {
    id: 'page:charts-pipeline',
    version: 1,
    title: 'Pipeline Status',
    kind: 'page',
    match: /^\/client-admin\/pipeline\/?$/,
    access: { gate: 'canAdminClient' }, // router: client-admin/*
    steps: [
      { id: 'title', target: target('page-header'), side: 'bottom', title: 'Pipeline Status', body: 'Live chart status per facility, specialty and provider.' },
      { id: 'tabs', target: target('pipeline.tabs'), side: 'bottom', title: 'Overview and Pipeline', body: '"Overview" summarizes counts and processing mode; "Pipeline" shows volume by bucket and average time at each stage.' },
      { id: 'filters', target: target('pipeline.filters'), side: 'bottom', title: 'Filters', body: 'Pick the clients, facilities, providers and specialties the cards below use.' },
    ],
  },
  {
    id: 'page:performance',
    version: 2,
    title: 'Performance Dashboard',
    kind: 'page',
    match: /^\/performance-dashboard\/?$/,
    access: { gate: 'canAdminClient' }, // router: performance-dashboard
    steps: [
      // The page's own rule (use-coder-performance-filters): team view vs self view.
      { id: 'header-self', target: target('page-header'), access: { when: (a) => !isPerformanceAdmin(a) }, side: 'bottom', title: 'Coder Performance Dashboard', body: 'Productivity, coding time and throughput across completed charts. You\'ll see your own numbers.' },
      { id: 'header-team', target: target('page-header'), access: { when: isPerformanceAdmin }, side: 'bottom', title: 'Coder Performance Dashboard', body: 'Coder productivity, coding time and throughput across completed charts.' },
      { id: 'content-self', target: target('performance.content'), access: { when: (a) => !isPerformanceAdmin(a) }, side: 'top', title: 'Drill in', body: 'Use the period at the top of the page to change the dates.' },
      { id: 'content', target: target('performance.content'), access: { when: isPerformanceAdmin }, side: 'top', title: 'Drill in', body: 'Use the filters at the top of the page to change the period, and compare coders side by side further down.' },
    ],
  },
  {
    id: 'page:operations',
    version: 1,
    title: 'Operations Dashboard',
    kind: 'page',
    match: /^\/operations-dashboard(\/import)?\/?$/,
    access: { gate: 'canViewOperations' }, // router: operations-dashboard/*
    steps: [
      { id: 'title', target: target('page-header'), side: 'bottom', title: 'Operations Dashboard', body: 'Health of the integration imports that bring charts in from the EMR.' },
      { id: 'batch-runs', target: target('ops.batch-runs'), side: 'top', title: 'Batch runs', body: 'Scheduled, missed and failed import runs for the selected range. Open a batch to see its trail.' },
      { id: 'charts', target: target('ops.charts'), side: 'top', title: 'Charts', body: 'How many charts were available, eligible and imported, and why the rest were not.' },
    ],
  },
  {
    id: 'page:analytics',
    version: 1,
    title: 'Analytics Dashboard',
    kind: 'page',
    match: /^\/engine-admin(\/overview)?\/?$/,
    access: { gate: 'canAdminEngine' }, // router: engine-admin/*
    steps: [
      { id: 'title', target: target('page-header'), side: 'bottom', title: 'Analytics Dashboard', body: 'How Codio-AI is classifying and coding your charts.' },
      { id: 'tabs', target: target('analytics.tabs'), access: { when: (a) => !a.canViewEngineInternals }, side: 'bottom', title: 'Accuracy views', body: 'Compare accuracy by client, facility or provider.' },
      { id: 'tabs-internal', target: target('analytics.tabs'), access: { gate: 'canViewEngineInternals' }, side: 'bottom', title: 'Accuracy views', body: 'Compare accuracy by client, facility or provider. "Costs" and "Flag Metrics" are internal views.' },
      { id: 'filters', target: target('analytics.filters'), side: 'bottom', title: 'Filters', body: 'Choose the client, facility and period the charts below use.' },
      { id: 'trend', target: target('analytics.trend'), whenMissing: 'An Autopilot accuracy trend appears here when there is Autopilot data for your filters.',  waitMs: 3000, side: 'top', title: 'Autopilot accuracy', body: 'How often Autopilot\'s codes were accepted as-is over time.' },
      { id: 'export', target: target('analytics.export'), side: 'bottom', title: 'Export', body: 'Downloads the report as a spreadsheet.' },
    ],
  },
  {
    id: 'page:billing-impact',
    version: 1,
    title: 'Billing Impact',
    kind: 'page',
    match: /^\/billing-impact(\/(summary|charts|provider|provider\/charts|uploads))?\/?$/,
    access: { when: (a) => a.can('billing_impact', 'read') }, // router: billing-impact/*
    steps: [
      { id: 'header', target: target('page-header'), side: 'bottom', title: 'Billing Impact', body: 'Compares the codes a client billed with what Codio AI predicted, priced on the CMS national benchmark.' },
      { id: 'tabs', target: target('billing-impact.tabs'), side: 'bottom', title: 'Views', body: '"Client vs. Codio AI" compares uploaded billing data, "Charts" lists the charts behind those numbers, "Provider vs. Codio AI" compares the codes sent with the chart, and "Uploads" lists the billing spreadsheets the numbers come from.' },
      { id: 'cards', target: target('billing-impact.cards'), whenMissing: 'Once billing data has been uploaded, cards here show the value Codio AI supports that wasn\'t billed, and billed codes it would not support.',  onlyOn: /^\/billing-impact(\/summary)?\/?$/, waitMs: 2500, side: 'bottom', title: 'The difference', body: 'Value Codio AI supports that wasn\'t billed, and billed codes it would not support.' },
    ],
  },
]
